<script setup lang="ts">
import { RouterView, useRoute, useRouter } from "vue-router";
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
  provide,
  defineAsyncComponent,
} from "vue";
import { auth, onAuthStateChanged, db, doc, getDoc } from "@/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
// Lazy load heavy modals (only loaded when needed)
const GoogleCalendarConnectModal = defineAsyncComponent(
  () => import("@/components/GoogleCalendarConnectModal.vue")
);
const PostTestModal = defineAsyncComponent(() => import("@/components/Student/PostTestModal.vue"));
const PreTestModal = defineAsyncComponent(() => import("@/components/Student/PreTestModal.vue"));
const InProgressSessionNotification = defineAsyncComponent(
  () => import("@/components/Student/InProgressSessionNotification.vue")
);
const PendingPostTestBanner = defineAsyncComponent(
  () => import("@/components/Student/PendingPostTestBanner.vue")
);
const AccountReviewModal = defineAsyncComponent(
  () => import("@/components/AccountReviewModal.vue")
);
const RatingWarningModal = defineAsyncComponent(
  () => import("@/components/RatingWarningModal.vue")
);
const FileReport = defineAsyncComponent(() => import("@/components/FileReport.vue"));
// Keep lightweight components as regular imports
import RouteLoadingBar from "@/components/RouteLoadingBar.vue";
import OfflinePopup from "@/components/OfflinePopup.vue";
import { shouldShowCalendarModal, markCalendarModalComplete } from "@/utils/googleCalendarUtils";
import { useSessionStatusMonitor } from "@/services/sessionStatusMonitor";
import { usePostTestDetection } from "@/composables/usePostTestDetection";
import { usePreTestDetection } from "@/composables/usePreTestDetection";
import { pendingPostTestService } from "@/services/pendingPostTestService";
import { usePostTestStore } from "@/stores/postTestStore";
import { logDebug, logError, logInfo, logWarn, logSuccess } from "@/utils/secureLogger";
import {
  checkAndFlagLowRating,
  shouldShowReviewModal,
  calculateUserAverageRating,
  flagUserForReview,
  clearReviewStatus,
  type UserRatingStatus,
} from "@/services/userRatingMonitor";
import Swal from "sweetalert2";
import "@/utils/testAuth";
import { useInactivityTimeout, performSecureLogout } from "@/composables/useInactivityTimeout";
import { useToast } from "@/composables/useToast";

// ✅ BUG FIX #4: Initialize post-test store
const postTestStore = usePostTestStore();

// ✅ Toast notifications
const { showError } = useToast();

const showGoogleCalendarModal = ref(false);
const currentUser = ref<any>(null);
const currentUserRole = ref<string | null>(null);
const route = useRoute();
const router = useRouter();
const isMandatoryModalShowing = ref(false);

// ✅ Periodic reminder check interval (every 6 hours)
let reminderCheckInterval: number | null = null;

// ✅ Account review state
const showAccountReviewModal = ref(false); // Suspension modal (≤2.5)
const showRatingWarningModal = ref(false); // ✅ NEW: Warning modal (2.6-2.8)
const userRatingStatus = ref<UserRatingStatus | null>(null);
const ratingCheckInProgress = ref(false);

// ✅ Reference to banner component for indicator integration
const pendingPostTestBannerRef = ref<InstanceType<typeof PendingPostTestBanner> | null>(null);

// ✅ NEW: Provide banner ref to child components (for notification icon in StudentHeader)
provide("pendingPostTestBannerRef", pendingPostTestBannerRef);

// Check if we're in development mode
const isDevelopment = computed(() => import.meta.env.DEV);

// Initialize post-test detection for students
const {
  showPostTestModal,
  completedSession,
  closeModal: closePostTestModal,
} = usePostTestDetection();

// ✅ Initialize pre-test detection for students
const {
  showPreTestModal,
  pendingSession,
  isMandatoryPreTest, // Track if pre-test is mandatory (admin-created session)
  startDetection: startPreTestDetection,
  stopDetection: stopPreTestDetection,
  closeModal: closePreTestModal,
  markPreTestCompleted,
} = usePreTestDetection();

// Initialize session status monitoring
const { startMonitoring, stopMonitoring } = useSessionStatusMonitor();

const { startTracking: startInactivityTracking, stopTracking: stopInactivityTracking } =
  useInactivityTimeout({
    onTimeout: async () => {
      logInfo("Logging out user due to inactivity", {
        userId: currentUser.value?.uid,
        role: currentUserRole.value,
      });
      await performSecureLogout("inactivity");
    },
  });

const resolveUserRole = async (userId: string): Promise<string | null> => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const data = userDoc.data() as { userType?: string | null };
      return data.userType || null;
    }
    logWarn("No Firestore user document found while resolving role", { userId });
    return null;
  } catch (error) {
    logError(
      "Failed to resolve user role",
      error instanceof Error ? error : new Error(String(error))
    );
    return null;
  }
};

// Check if user should see the modal when they're not authorized
const checkAndShowModal = async (user: any) => {
  if (!user) return;

  // Don't show on OAuth callback page to avoid interference
  if (route?.path === "/oauth-callback") return;

  // Check if user needs to authorize Google Calendar
  const shouldShow = await shouldShowCalendarModal(user.uid);
  if (shouldShow) {
    logDebug("Showing Google Calendar modal - tutor authorization required");
    showGoogleCalendarModal.value = true;
  }
};

// Check for mandatory pending tests and block navigation if needed
const checkMandatoryTests = async () => {
  if (!currentUser.value || isMandatoryModalShowing.value) return;

  // ✅ NEW: Skip if account is under review
  if (showAccountReviewModal.value) return;

  try {
    // Update mandatory statuses for all pending tests
    await pendingPostTestService.updateAllMandatoryStatuses(currentUser.value.uid);

    // Check for mandatory test
    const hasMandatory = await pendingPostTestService.hasMandatoryPendingPostTest(
      currentUser.value.uid
    );

    if (hasMandatory) {
      const nextTest = await pendingPostTestService.getNextPendingPostTest(currentUser.value.uid);

      if (nextTest?.isMandatory) {
        // ✅ CRITICAL FIX: Verify the session wasn't cancelled or rescheduled
        logDebug("Checking session status before showing mandatory modal", {
          sessionId: nextTest.sessionId,
        });

        const sessionDocRef = doc(db, "sessions", nextTest.sessionId);
        const sessionSnapshot = await getDoc(sessionDocRef);

        if (sessionSnapshot.exists()) {
          const sessionData = sessionSnapshot.data();

          // ✅ FIX: If session was cancelled or rescheduled, clear the pending test and don't show modal
          if (
            sessionData.sessionStatus === "cancelled" ||
            sessionData.cancelledAt ||
            sessionData.rescheduleReason
          ) {
            logWarn("Session was cancelled/rescheduled - clearing stale pending post-test", {
              sessionId: nextTest.sessionId,
              status: sessionData.sessionStatus,
              reason: sessionData.cancellationReason || sessionData.rescheduleReason,
            });

            try {
              await pendingPostTestService.clearPendingPostTest(nextTest.sessionId);
              logSuccess("Cleared pending post-test for cancelled/rescheduled session");
            } catch (clearError) {
              logError("Failed to clear stale pending record", clearError);
            }

            // Don't show the modal
            return;
          }
        }

        // ✅ CRITICAL FIX: Before showing modal, verify this session doesn't already have a completed post-test
        // This prevents the modal from re-appearing after completion due to Firestore propagation delays
        logDebug("Checking if post-test already exists for this session", {
          sessionId: nextTest.sessionId,
        });

        const postTestsQuery = query(
          collection(db, "post_tests"),
          where("session_id", "==", nextTest.sessionId),
          where("user_id", "==", currentUser.value.uid)
        );
        const postTestsSnapshot = await getDocs(postTestsQuery);

        if (!postTestsSnapshot.empty) {
          logWarn("Post-test already completed for this session - clearing stale pending record", {
            sessionId: nextTest.sessionId,
            postTestId: postTestsSnapshot.docs[0].id,
          });

          // Clear the stale pending record
          try {
            await pendingPostTestService.clearPendingPostTest(nextTest.sessionId);
            logSuccess("Cleared stale pending post-test record");
          } catch (clearError) {
            logError("Failed to clear stale pending record", clearError);
          }

          // Don't show the modal
          return;
        }

        logDebug(
          "No existing post-test found and session is valid - proceeding with mandatory modal"
        );
        logWarn("Mandatory post-test detected - showing modal", {
          sessionId: nextTest.sessionId,
          deadline: nextTest.deadline?.toDate()?.toISOString(),
        });

        isMandatoryModalShowing.value = true;

        // Set up mandatory modal
        completedSession.value = {
          sessionId: nextTest.sessionId,
          subject: nextTest.sessionSubject,
          tutorId: nextTest.tutorId,
          tutorName: nextTest.tutorName,
          startAt: nextTest.sessionDate,
          endAt: nextTest.sessionDate,
          completedAt: nextTest.sessionDate,
          isMandatory: true,
          deadline: nextTest.deadline,
        };

        showPostTestModal.value = true;

        // ✅ FIX: Don't block interactions here - PostTestModal handles its own blocking
        // The modal's setupMandatoryBlocking() will handle all interaction prevention

        // Show initial warning
        await Swal.fire({
          icon: "warning",
          title: "⏰ Mandatory Post-Test Required",
          html: `
            <div class="text-left">
              <p class="mb-3">The deadline for your post-test has passed.</p>
              <div class="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
                <p class="text-sm font-semibold text-red-900 mb-2">⚠️ You must complete this test to continue</p>
                <ul class="list-disc list-inside text-sm text-red-800 space-y-1">
                  <li>Subject: <strong>${nextTest.sessionSubject}</strong></li>
                  <li>Tutor: ${nextTest.tutorName}</li>
                  <li>Deadline: ${nextTest.deadline?.toDate()?.toLocaleDateString()}</li>
                </ul>
              </div>
              <p class="text-sm text-gray-600">All other actions are blocked until you complete this assessment.</p>
            </div>
          `,
          confirmButtonText: "Start Post-Test",
          confirmButtonColor: "#dc2626",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        // ✅ FIX: Remove the pointer-events manipulation
        // The PostTestModal will be visible after this alert is dismissed
        // and will handle all blocking via setupMandatoryBlocking()
      }
    }
  } catch (error) {
    logError("Error checking mandatory tests", error);
  }
};

// ✅ Check user rating and show appropriate modal (ONLY ONCE ON LOGIN)
const checkUserRating = async () => {
  // Guard: ensure user is authenticated
  if (!currentUser.value || !auth.currentUser) {
    logDebug("Skipping rating check - no authenticated user");
    return;
  }

  // Guard: prevent duplicate checks if already showing or already checked
  if (showAccountReviewModal.value || showRatingWarningModal.value || ratingCheckInProgress.value) {
    logDebug("Skipping rating check - modal already showing or check in progress");
    return;
  }

  ratingCheckInProgress.value = true;

  try {
    logDebug("Checking user rating for account review", { userId: currentUser.value.uid });

    // First check if already flagged for review (suspended)
    const needsReviewModal = await shouldShowReviewModal(currentUser.value.uid);

    // Always calculate current rating to check if it has improved
    const ratingStatus = await calculateUserAverageRating(currentUser.value.uid);

    if (needsReviewModal) {
      // User was previously flagged - check if rating has improved
      if (ratingStatus.averageRating > 2.5 && ratingStatus.totalRatings > 0) {
        // ✅ Rating has improved above 2.5 - clear the suspension
        await clearReviewStatus(currentUser.value.uid);
        showAccountReviewModal.value = false;

        logInfo("User rating improved above 2.5 - clearing suspension", {
          userId: currentUser.value.uid,
          averageRating: ratingStatus.averageRating,
          totalRatings: ratingStatus.totalRatings,
        });

        // Show success message
        await Swal.fire({
          icon: "success",
          title: "Account Suspension Lifted!",
          text: `Your rating has improved to ${ratingStatus.averageRating.toFixed(
            1
          )}/5.0. Your account is no longer under review.`,
          confirmButtonColor: "#10b981",
        });

        return;
      }

      // Rating is still ≤2.5 - keep showing suspension modal
      userRatingStatus.value = ratingStatus;
      showAccountReviewModal.value = true;
      logWarn("Showing account suspension modal - user still flagged", {
        userId: currentUser.value.uid,
        averageRating: ratingStatus.averageRating,
      });
      return;
    }

    // ✅ Only check if user has AT LEAST 1 rating
    if (ratingStatus.totalRatings === 0) {
      logDebug("User has no ratings yet - skipping review check", {
        userId: currentUser.value.uid,
      });
      return;
    }

    // ✅ NEW: Priority 1 - Suspension (≤2.5)
    if (ratingStatus.needsReview && !ratingStatus.isUnderReview) {
      // Flag the account first
      await flagUserForReview(currentUser.value.uid, ratingStatus);

      // Then show suspension modal
      userRatingStatus.value = ratingStatus;
      showAccountReviewModal.value = true;

      logWarn("User rating dropped to 2.5 or below - showing suspension modal", {
        userId: currentUser.value.uid,
        averageRating: ratingStatus.averageRating,
        totalRatings: ratingStatus.totalRatings,
      });
    }
    // ✅ NEW: Priority 2 - Warning (2.6-2.8)
    else if (ratingStatus.needsWarning) {
      // Show warning modal (dismissible, shows every login)
      userRatingStatus.value = ratingStatus;
      showRatingWarningModal.value = true;

      logWarn("User rating in warning zone (2.6-2.8) - showing warning modal", {
        userId: currentUser.value.uid,
        averageRating: ratingStatus.averageRating,
        totalRatings: ratingStatus.totalRatings,
      });
    }
  } catch (error) {
    logError("Error checking user rating", error);
  } finally {
    ratingCheckInProgress.value = false;
  }
};

// Watch for route changes
watch(
  () => route?.path,
  async () => {
    if (currentUser.value) {
      // Skip all checks if account is under review
      if (showAccountReviewModal.value) return;

      await checkAndShowModal(currentUser.value);

      // ✅ FIX: Only check for mandatory tests when on student pages
      if (isOnStudentPage.value) {
        await checkMandatoryTests();
      }
    }
  }
);

// ✅ BUG FIX #4: Watch store for queue changes and open next test
watch(
  () => postTestStore.currentTest,
  (newTest) => {
    if (newTest && !showPostTestModal.value) {
      logInfo("Store triggered new post-test modal", {
        sessionId: newTest.sessionId,
        subject: newTest.sessionSubject,
      });

      // Set up modal with test from store
      completedSession.value = {
        sessionId: newTest.sessionId,
        subject: newTest.sessionSubject,
        tutorId: newTest.tutorId,
        tutorName: newTest.tutorName,
        startAt: newTest.sessionDate,
        endAt: newTest.sessionDate,
        completedAt: newTest.sessionDate,
        isMandatory: newTest.isMandatory,
        deadline: newTest.deadline,
      };

      showPostTestModal.value = true;
    }
  }
);

// ✅ FIX: Helper to check if user is on a student page
const isOnStudentPage = computed(() => {
  const studentPaths = [
    "/student/find-tutors",
    "/student/sessions",
    "/student/dashboard",
    "/student/profile",
    "/student/settings",
  ];
  return studentPaths.some((path) => route?.path?.startsWith(path));
});

// Check authentication state and show modal if needed
onMounted(() => {
  onAuthStateChanged(auth, async (user) => {
    currentUser.value = user;
    if (user) {
      currentUserRole.value = await resolveUserRole(user.uid);

      if (currentUserRole.value === "student" || currentUserRole.value === "tutor") {
        startInactivityTracking();
      } else {
        stopInactivityTracking();
      }

      // ✅ NEW: Check rating first before anything else
      await checkUserRating();

      // Skip other checks if account is under review
      if (showAccountReviewModal.value) return;

      await checkAndShowModal(user);

      // ✅ FIX: Only check for mandatory tests AFTER user navigates to student pages
      // This prevents the modal from showing on the login page
      if (isOnStudentPage.value) {
        await checkMandatoryTests();
      }

      // Defer session monitoring to not block initial page load
      setTimeout(() => {
        startMonitoring();
      }, 500);

      // ✅ Defer pre-test detection for students to not block initial render
      if (currentUserRole.value === "student") {
        setTimeout(() => {
          startPreTestDetection();
          logInfo("🎓 Started pre-test detection for student");
        }, 1000);

        // ✅ Watch for mandatory pre-tests and show warning
        watch(
          isMandatoryPreTest,
          async (isMandatory) => {
            if (isMandatory && showPreTestModal.value && pendingSession.value) {
              // Show blocking warning for mandatory pre-test
              await Swal.fire({
                icon: "warning",
                title: "⚠️ Required Pre-Test",
                html: `
                  <div class="text-left">
                    <p class="mb-3">Your tutor has scheduled a session with you!</p>
                    <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
                      <p class="text-sm font-semibold text-blue-900 mb-2">📚 Session Details</p>
                      <ul class="list-disc list-inside text-sm text-blue-800 space-y-1">
                        <li>Subject: <strong>${pendingSession.value.sessionSubject}</strong></li>
                        <li>Tutor: ${pendingSession.value.tutorName}</li>
                        <li>Date: ${pendingSession.value.sessionDate.toLocaleDateString()}</li>
                      </ul>
                    </div>
                    <p class="text-sm text-gray-600">You must complete a pre-test before the session can be confirmed. This helps your tutor understand your current knowledge level.</p>
                  </div>
                `,
                confirmButtonText: "Start Pre-Test",
                confirmButtonColor: "#3b82f6",
                allowOutsideClick: false,
                allowEscapeKey: false,
              });
            }
          },
          { immediate: true }
        );
      }
    } else {
      showGoogleCalendarModal.value = false;
      isMandatoryModalShowing.value = false;
      showAccountReviewModal.value = false; // ✅ Reset suspension modal
      showRatingWarningModal.value = false; // ✅ NEW: Reset warning modal
      userRatingStatus.value = null; // ✅ Reset rating status
      currentUserRole.value = null;
      // Stop session monitoring when user logs out
      stopMonitoring();
      // ✅ Stop pre-test detection when user logs out
      stopPreTestDetection();
      stopInactivityTracking();
    }
  });

  // Check for mandatory tests when user returns to tab
  document.addEventListener("visibilitychange", async () => {
    if (!document.hidden && currentUser.value) {
      logDebug("User returned to tab - checking for mandatory tests");
      // Skip checks if account is under review or not on student page
      if (!showAccountReviewModal.value && isOnStudentPage.value) {
        await checkMandatoryTests();
      }
    }
  });
});

// ✅ Cleanup on component unmount
onBeforeUnmount(() => {
  stopPreTestDetection();
  stopInactivityTracking();
  logDebug("App component unmounted, cleaned up all intervals and listeners");
});

const handleGoogleCalendarClose = () => {
  showGoogleCalendarModal.value = false;
};

const handleGoogleCalendarConnected = async () => {
  showGoogleCalendarModal.value = false;
  logInfo("Google Calendar connected!");

  // Mark the user as having connected Google Calendar
  if (currentUser.value) {
    try {
      const { markCalendarModalComplete } = await import("@/utils/googleCalendarUtils");
      await markCalendarModalComplete(currentUser.value.uid, true);
    } catch (error) {
      logError("Error marking calendar connection:", error);
    }
  }
};

const handleGoogleCalendarSkipped = async () => {
  showGoogleCalendarModal.value = false;
  logDebug("User skipped Google Calendar connection - will see modal again until they connect");

  // Don't mark as complete for authorization prompts - user should see it again until they connect
  // This ensures the modal keeps appearing for unauthorized users
};

// ✅ BUG FIX #4: Handle post-test modal close via store
const handlePostTestClose = () => {
  logInfo("Post-test modal closing", {
    wasMandatory: isMandatoryModalShowing.value,
    currentStep: completedSession.value ? "has-session" : "no-session",
  });

  if (isMandatoryModalShowing.value) {
    // ✅ FIX: Clean up mandatory modal state
    // Note: We don't manipulate pointer-events here anymore
    // The PostTestModal's cleanupMandatoryBlocking() handles all event listener cleanup
    isMandatoryModalShowing.value = false;
    logDebug("Reset isMandatoryModalShowing flag");
  }

  // Close via store for consistent state management
  postTestStore.closePostTest();
  closePostTestModal();

  // ✅ CRITICAL FIX: After closing, re-check for remaining pending tests
  // This ensures the next test in queue shows up if there are multiple pending tests
  setTimeout(async () => {
    if (currentUser.value && isOnStudentPage.value) {
      logDebug("Re-checking for pending tests after modal close");
      await checkMandatoryTests();
    }
  }, 1000); // 1 second delay to allow Firestore propagation
};

// ✅ NEW: Handle account review modal close (only happens after logout)
const handleAccountReviewClose = () => {
  showAccountReviewModal.value = false;
  userRatingStatus.value = null;
};

// ✅ NEW: Handle pre-test completion
const handlePreTestComplete = async (preTestData: any) => {
  logInfo("Pre-test completed", {
    sessionId: pendingSession.value?.sessionId,
    score: preTestData?.score,
    isMandatory: isMandatoryPreTest.value,
  });

  try {
    // Mark pre-test as completed in session document
    if (pendingSession.value?.sessionId) {
      await markPreTestCompleted(pendingSession.value.sessionId);
      logSuccess("Pre-test marked as completed in session document");
    }

    // ✅ Fetch actual session data from Firestore for correct subject and tutor info
    let sessionSubject = "N/A";
    let tutorName = "N/A";

    if (pendingSession.value?.sessionId) {
      try {
        const sessionDoc = await getDoc(doc(db, "sessions", pendingSession.value.sessionId));
        if (sessionDoc.exists()) {
          const sessionData = sessionDoc.data();
          sessionSubject = sessionData.subject || "N/A";

          // Fetch tutor name from users collection
          if (sessionData.tutorId) {
            const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
            if (tutorDoc.exists()) {
              const tutorData = tutorDoc.data();
              const firstName = tutorData.firstName || "";
              const lastName = tutorData.lastName || "";
              tutorName = `${firstName} ${lastName}`.trim() || "N/A";
            }
          }
        }
      } catch (fetchError) {
        logError("Error fetching session details for completion modal", fetchError);
      }
    }

    // ✅ Calculate score as fraction (e.g., "3/10" instead of percentage)
    const correctAnswers = preTestData?.score || 0;
    const totalQuestions = preTestData?.totalQuestions || 10;
    const scoreDisplay = `${correctAnswers}/${totalQuestions}`;

    // ✅ Reset mandatory flag BEFORE closing to allow closure
    isMandatoryPreTest.value = false;

    // Close modal
    closePreTestModal();

    // Show success message with session details
    await Swal.fire({
      icon: "success",
      title: "✅ Pre-Test Completed!",
      html: `
        <div class="text-left">
          <p class="mb-3">Your pre-test has been successfully submitted!</p>
          <div class="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-3">
            <p class="text-sm font-semibold text-green-900 mb-2">📊 Results</p>
            <ul class="list-disc list-inside text-sm text-green-800 space-y-1">
              <li>Score: <strong>${scoreDisplay}</strong></li>
              <li>Subject: <strong>${sessionSubject}</strong></li>
              <li>Tutor: ${tutorName}</li>
            </ul>
          </div>
          <p class="text-sm text-gray-600">Your session is now confirmed! You can view it in your dashboard.</p>
        </div>
      `,
      confirmButtonText: "View My Sessions",
      confirmButtonColor: "#10b981",
    });

    // ✅ Navigate to sessions page after user clicks "View My Sessions"
    router.push("/student/sessions");

    // Reload pending tests in case there are more
    if (pendingPostTestBannerRef.value) {
      pendingPostTestBannerRef.value.loadPendingTests();
    }

    logSuccess("Pre-test completed successfully!");
  } catch (error) {
    logError("Error handling pre-test completion", error);
    showError("Failed to complete pre-test. Please try again.");
  }
};

// ✅ NEW: Handle pre-test modal close
const handlePreTestClose = () => {
  // Prevent closing if mandatory
  if (isMandatoryPreTest.value) {
    logWarn("Cannot close mandatory pre-test modal");
    Swal.fire({
      icon: "warning",
      title: "Pre-Test Required",
      text: "You must complete this pre-test before continuing. The session cannot be confirmed without it.",
      confirmButtonText: "Continue Pre-Test",
      confirmButtonColor: "#3b82f6",
    });
    return;
  }

  logInfo("Pre-test modal closed without completion");
  closePreTestModal();
};
</script>

<template>
  <!-- Route Loading Bar -->
  <RouteLoadingBar />

  <RouterView v-slot="{ Component, route }">
    <keep-alive :include="['StudentMessagesView', 'TutorMessagesView']">
      <component :is="Component" :key="route.path" />
    </keep-alive>
  </RouterView>

  <!-- ✅ NEW: Global Pending Post-Test Banner (appears on all pages for students) -->
  <PendingPostTestBanner ref="pendingPostTestBannerRef" />

  <!-- ✅ In-Progress Session Notification (appears globally for students with active sessions) -->
  <InProgressSessionNotification />

  <!-- ✅ Rating Warning Modal (dismissible, for rating 2.6-2.8) -->
  <RatingWarningModal
    v-if="userRatingStatus && showRatingWarningModal"
    :is-open="showRatingWarningModal"
    :average-rating="userRatingStatus.averageRating"
    :total-ratings="userRatingStatus.totalRatings"
    @close="showRatingWarningModal = false"
  />

  <!-- ✅ Account Suspension Modal (blocking, for rating ≤2.5) -->
  <AccountReviewModal
    v-if="userRatingStatus"
    :is-open="showAccountReviewModal"
    :average-rating="userRatingStatus.averageRating"
    :total-ratings="userRatingStatus.totalRatings"
    @close="handleAccountReviewClose"
  />

  <GoogleCalendarConnectModal
    :open="showGoogleCalendarModal"
    :is-first-login="false"
    :show-skip="true"
    @close="handleGoogleCalendarClose"
    @connected="handleGoogleCalendarConnected"
    @skipped="handleGoogleCalendarSkipped"
  />

  <!-- Post-Test Modal for Students -->
  <PostTestModal
    v-if="completedSession"
    :is-open="showPostTestModal"
    :session-details="{
      sessionId: completedSession.sessionId,
      subject: completedSession.subject,
      tutorId: completedSession.tutorId,
      tutorName: completedSession.tutorName || 'Tutor',
      date: completedSession.startAt,
      deadline: completedSession.deadline,
    }"
    @close="handlePostTestClose"
  />

  <!-- ✅ Pre-Test Modal for Students (Admin-Created Sessions) -->
  <PreTestModal
    v-if="pendingSession"
    :is-open="showPreTestModal"
    :session-id="pendingSession.sessionId"
    :session-subject="pendingSession.sessionSubject"
    :learning-goals="pendingSession.learningGoals"
    :is-mandatory="isMandatoryPreTest"
    @complete="handlePreTestComplete"
    @close="handlePreTestClose"
  />

  <!-- Global offline popup -->
  <OfflinePopup />

  <!-- File Report Modal (Global) -->
  <FileReport />
</template>

<style scoped>
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>
